<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import bubbles from '~/assets/images/bubbles/firefly-bubbles-2@2x.webp'

defineProps<{
  services: SectionServicesListRecord['services']
  layout: SectionServicesListRecord['layout']
}>()

const { isDesktop } = useDevice()
const activeIdx = ref<number | null>(null)
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: ctxRef.value,
        start: 'top-=200px center',
        end: `endcenter`,
        markers: false,
        onEnter: () => {
          activeIdx.value = 0
        }
      })
      ScrollTrigger.refresh()
      ScrollTrigger.sort()
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="col-span-full flex flex-col gap-2 lg:col-span-9 lg:col-start-3 lg:gap-4 xl:col-start-4"
  >
    <LazyImage
      v-if="isDesktop"
      class="absolute -bottom-28 -left-96 hidden aspect-square object-contain lg:block xl:-left-80"
      :src="bubbles"
      :width="728"
      :height="728"
      alt=""
    />
    <DeliciousSausage
      v-if="isDesktop"
      name="services-medium"
      class="absolute -left-96 bottom-80 hidden lg:block xl:-left-80"
    />
    <ServicesPanel
      v-for="(service, idx) in services"
      :key="idx"
      :idx="idx"
      :active="activeIdx === idx"
      :layout="layout"
      class="relative"
      @expand="activeIdx = activeIdx !== idx ? idx : null"
    >
      <template #title
        ><span v-if="service.title" class="block">
          {{ service.title }}
        </span></template
      >
      <template #content>
        <div
          class="w-full py-4 lg:absolute lg:left-1/2 lg:top-0 lg:max-w-md lg:-translate-y-1/2 lg:py-0"
        >
          <Transition name="servicesMedium">
            <Image
              v-if="service.image && activeIdx === idx"
              effect="mask"
              class="mb-4 aspect-16/10 w-full object-cover lg:mb-10"
              :src="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.src
                  : service.image.url
              "
              :srcset="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.webpSrcSet
                  : undefined
              "
              :width="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.width
                  : (service.image.width ?? 448)
              "
              :height="
                service.image.responsiveImage
                  ? service.image.responsiveImage?.height
                  : (service.image.height ?? 280)
              "
              :alt="service.image?.alt ?? ''"
              sizes="auto"
            />
          </Transition>
          <Transition name="servicesMediumText">
            <div
              v-if="service.description && activeIdx === idx"
              class="text-sm lg:text-lg"
              v-html="service.description"
            />
          </Transition>
        </div>
      </template>
    </ServicesPanel>
  </div>
</template>
